import { getPostBySlug, getPostsBySection, getHomeData } from '@/lib/api';
import { notFound } from 'next/navigation';
import Breadcrumb from '@/components/ui/Breadcrumb';
import Label from '@/components/ui/Label';
import ShareBar from '@/components/ui/ShareBar';
import PullQuote from '@/components/ui/PullQuote';
import KeyFactsBox from '@/components/ui/KeyFactsBox';
import StoryCard from '@/components/ui/StoryCard';
import SectionHeader from '@/components/ui/SectionHeader';
import MostRead from '@/components/modules/MostRead';
import Link from 'next/link';
import type { Metadata } from 'next';

export async function generateMetadata({ params }: { params: Promise<{ slug: string }> }): Promise<Metadata> {
  const { slug } = await params;
  const story = await getPostBySlug(slug);
  return {
    title: story?.title ?? 'Article',
    description: story?.deck,
  };
}

export default async function ArticlePage({ params }: { params: Promise<{ slug: string }> }) {
  const { slug } = await params;
  const story = await getPostBySlug(slug);
  if (!story) notFound();

  const author = story.authorDetails;
  const { mostRead, latest } = await getHomeData();
  const relatedStories = await getPostsBySection(story.section, undefined, 4);
  const sidebarStories = relatedStories.filter(s => s.slug !== story.slug).slice(0, 4);

  const articleFacts = [
    `Negotiations began on ${story.publishedAt}`,
    '32 nations are represented at the talks',
    'The session has lasted longer than any previous round',
    'A 48-hour ceasefire has been agreed as a confidence-building step',
    'The UN Secretary-General is presiding as chief mediator',
  ];

  return (
    <div className="max-w-[1400px] mx-auto px-5 py-8">
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-12 max-w-[1100px] mx-auto">

        {/* ARTICLE MAIN */}
        <article>
          <Breadcrumb items={[{ label: 'Home', href: '/' }, { label: story.section, href: '/world' }, { label: story.title.slice(0, 30) + '...' }]} />

          <div className="mb-3">
            <Link href="/world">
              <span className="font-['IBM_Plex_Sans',system-ui,sans-serif] font-bold text-[11px] tracking-[0.12em] uppercase text-[#C8102E] hover:text-[#9E0C23] transition-colors cursor-pointer">
                {story.section}
              </span>
            </Link>
            {story.label && (
              <span className="ml-3"><Label type={story.label} animate={story.isLive} /></span>
            )}
          </div>

          <h1 className="font-['Playfair_Display',Georgia,serif] font-bold text-[32px] lg:text-[44px] leading-[1.12] text-[#0D0D0D] mb-4">
            {story.title}
          </h1>

          <p className="font-['Source_Serif_4',Georgia,serif] text-[19px] italic leading-[1.55] text-[#2D2D2D] mb-5">
            {story.deck}
          </p>

          {/* Meta bar */}
          <div className="border-y border-[#D4D4D4] py-3.5 my-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 flex-wrap">
            <div className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[12px] text-[#6B6B6B]">
              By{' '}
              <Link href={`/author/${story.authorSlug}`} className="font-bold text-[#0D0D0D] hover:text-[#C8102E] transition-colors">
                {story.author}
              </Link>
              {author && <span className="text-[#9A9A9A]"> · {author.role}</span>}
            </div>
            <span className="hidden sm:block text-[#D4D4D4]">|</span>
            <span className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[12px] text-[#6B6B6B]">Published: {story.publishedAt}</span>
            {story.updatedAt && (
              <>
                <span className="hidden sm:block text-[#D4D4D4]">|</span>
                <span className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[12px] text-[#6B6B6B]">{story.updatedAt}</span>
              </>
            )}
            <span className="hidden sm:block text-[#D4D4D4]">|</span>
            <span className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[12px] text-[#6B6B6B]">{story.readingTime} min read</span>
            <ShareBar />
          </div>

          {/* Hero image */}
          <div 
            className={`${story.imageBg?.startsWith('bg-') ? story.imageBg : 'bg-gray-100'} aspect-video w-full mb-2 bg-cover bg-center bg-no-repeat`} 
            style={!story.imageBg?.startsWith('bg-') ? { backgroundImage: `url(${story.imageBg})` } : {}}
          />
          <p className="font-['IBM_Plex_Sans',system-ui,sans-serif] italic text-[12px] text-[#6B6B6B] mb-6 pb-3 border-b border-[#D4D4D4]">
            Delegates arrive at the Palais des Nations, Geneva. Photo: Veritas News / Reuters
          </p>

          {/* Article body */}
          <div className="article-prose">
            {story.body ? (
              <div dangerouslySetInnerHTML={{ __html: story.body }} />
            ) : (
              <>
                <p>
                  After fourteen hours of closed-door negotiations at the Palais des Nations in Geneva, a moment of unexpected progress emerged in the early afternoon. Delegates from thirty-two nations — representing the most comprehensive diplomatic format assembled since the conflict began — agreed to a 48-hour pause in hostilities as a confidence-building measure, paving the way for what mediators described as "the most substantive peace framework since 2019."
                </p>
                <p>
                  The announcement, delivered by the UN Secretary-General in a brief statement delivered outside the main assembly hall, was greeted with cautious optimism by observers who have watched previous attempts at negotiation collapse within days of their announcement. The difference, those close to the talks say, lies in the architecture of the agreement itself.
                </p>

                <KeyFactsBox facts={articleFacts} />

                <p>
                  Unlike the ceasefire frameworks of 2019 and 2021 — which collapsed partly because major regional powers refused to act as guarantors — the Geneva process includes co-signatories from all principal regional actors. This, analysts say, changes the incentive structure in a way that previous attempts did not.
                </p>
                <p>
                  "The key difference is that every party with the capacity to spoil this agreement has signed on to monitor it," said one senior diplomat, speaking on condition of anonymity because they were not authorised to brief the press. "That has never been true before."
                </p>

                <h2>Why now?</h2>

                <p>
                  The timing of the breakthrough has surprised many who study the conflict closely. Three months ago, the prospect of any negotiations seemed remote. What changed, according to multiple sources, was a combination of military fatigue on both sides and a new American diplomatic strategy that prioritised back-channel engagement over public messaging.
                </p>
                <p>
                  Satellite imagery released by independent analysts at the Bellingcat collective on Tuesday morning appeared to confirm a partial troop withdrawal from the most contested region — a move that, while symbolic, was described by the lead mediator as consistent with pre-agreed confidence-building protocols.
                </p>

                <PullQuote
                  quote="We are not celebrating yet, but we are closer than we have ever been. The gap has narrowed to a point where we believe a durable framework is achievable."
                  attribution="French Foreign Minister, speaking outside the Palais des Nations"
                />

                <p>
                  What remains unclear is whether the 48-hour pause will hold long enough for the technical annexes to the agreement — covering prisoner exchanges, humanitarian corridor access, and monitoring mechanisms — to be finalised. Negotiators from both sides have publicly committed to remaining at the table, but sources close to the talks acknowledge that several critical issues remain unresolved.
                </p>
                <p>
                  Among the most contentious: the composition of the international monitoring force, the timeline for any broader withdrawal, and the question of what — if anything — will fill the governance vacuum in the territories affected.
                </p>

                <div className="border-l-[3px] border-[#D4D4D4] bg-[#F5F4F0] pl-4 py-2 my-5">
                  <span className="font-['IBM_Plex_Sans',system-ui,sans-serif] font-bold text-[11px] text-[#6B6B6B] uppercase tracking-[0.06em]">Related: </span>
                  <Link href="/article/nato-artillery-stockpiles" className="font-['Playfair_Display',Georgia,serif] font-semibold text-[15px] text-[#1A4A8A] hover:underline">
                    NATO allies agree to double artillery stockpiles following defence summit
                  </Link>
                </div>

                <p>
                  Our photographer, who was granted rare access to the corridors outside the negotiating rooms, described the atmosphere as one of exhausted determination — delegates who had clearly not slept, moving between small group meetings with the focused urgency of people who understood the stakes.
                </p>

                <h2>What happens next</h2>

                <p>
                  The next 48 hours will be decisive. Both sides have agreed to a technical working group that will meet continuously through the night to address the outstanding annexes. A senior UN official said the goal was to have a full framework ready for signature by Thursday morning — though they were careful to add that timelines in negotiations of this complexity are rarely met.
                </p>
                <p>
                  What is clear is that the diplomatic environment has shifted in a way that has not been seen in years. Whether that shift produces a durable peace depends on decisions that will be made in the coming hours by people under extraordinary pressure, in rooms that the world cannot see.
                </p>
                <p>
                  For the civilians whose lives have been shaped by this conflict, the 48-hour pause is not peace. It is the possibility of a conversation about peace. And for now, in Geneva, that is considered progress.
                </p>
              </>
            )}
          </div>

          {/* Tags */}
          <div className="flex flex-wrap gap-2 mt-8 pt-5 border-t border-[#D4D4D4]">
            {story.tags.map(tag => (
              <Link key={tag} href={`/topic/${tag.toLowerCase().replace(/ /g, '-')}`}>
                <span className="border border-[#D4D4D4] px-3 py-1.5 text-[12px] font-['IBM_Plex_Sans',system-ui,sans-serif] text-[#2D2D2D] hover:bg-[#1A1A1A] hover:text-white hover:border-[#1A1A1A] transition-all duration-150 cursor-pointer block">
                  {tag}
                </span>
              </Link>
            ))}
          </div>

          {/* More from section */}
          <div className="mt-10">
            <SectionHeader title={`More from ${story.section}`} moreLink="/world" moreLabel="All stories →" />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {relatedStories.map(s => <StoryCard key={s.id} story={s} size="medium" />)}
            </div>
          </div>
        </article>

        {/* STICKY SIDEBAR */}
        <aside className="hidden lg:block">
          <div className="sticky top-[100px] self-start space-y-8">
            {/* Also in section */}
            <div>
              <div className="pb-2.5 border-b-[2.5px] border-[#0D0D0D] mb-4">
                <h3 className="font-['IBM_Plex_Sans',system-ui,sans-serif] font-bold text-[12px] tracking-[0.1em] uppercase">Also in {story.section}</h3>
              </div>
              {sidebarStories.map(s => (
                <Link key={s.id} href={`/article/${s.slug}`} className="block py-3 border-b border-[#D4D4D4] hover:opacity-75 transition-opacity group">
                  <h4 className="font-['Playfair_Display',Georgia,serif] font-semibold text-[13px] leading-[1.35] group-hover:text-[#C8102E] transition-colors">{s.title}</h4>
                  <p className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[11px] text-[#6B6B6B] mt-1">{s.publishedAt}</p>
                </Link>
              ))}
            </div>

            {latest.filter(s => s.label === 'analysis')[0] && (
              <div className="border-t-[2.5px] border-[#0D0D0D] pt-3.5">
                <div className="mb-2"><Label type="analysis" /></div>
                <Link href={`/article/${latest.filter(s => s.label === 'analysis')[0].slug}`} className="group hover:opacity-75 transition-opacity block">
                  <h4 className="font-['Playfair_Display',Georgia,serif] font-bold text-[15px] leading-[1.3] group-hover:text-[#C8102E] transition-colors">
                    {latest.filter(s => s.label === 'analysis')[0].title}
                  </h4>
                </Link>
              </div>
            )}

            <MostRead initialData={mostRead} />

            {/* Newsletter mini */}
            <div className="border border-[#D4D4D4] p-4">
              <h3 className="font-['Playfair_Display',Georgia,serif] font-bold text-[16px] mb-1">Morning Briefing</h3>
              <p className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[12px] text-[#6B6B6B] mb-3">Start your day with the five stories that matter.</p>
              <div className="flex border border-[1.5px] border-[#0D0D0D] overflow-hidden">
                <input type="email" placeholder="Your email" className="flex-1 px-2 py-2 font-['IBM_Plex_Sans',system-ui,sans-serif] text-[12px] outline-none bg-transparent" />
                <button className="bg-[#C8102E] text-white px-3 py-2 font-['IBM_Plex_Sans',system-ui,sans-serif] font-semibold text-[11px] hover:bg-[#9E0C23] transition-colors">
                  Subscribe
                </button>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
